import React, { useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import africaTopo from "@/data/africa-110m.json";
import { WAF_COUNTRIES, WAF_COUNTRY_NAMES, WAF_TEAM, WAF_COUNTRY_AMS } from "@/data/waf-region";
import { Mail, Phone, Linkedin, MapPin, Users, Building2, Cpu, Briefcase, LifeBuoy, ChevronDown, ArrowLeft } from "lucide-react";
import { WhatsappIcon, waLink } from "@/lib/whatsapp";

const WAF_FILL = "#D42B1E";       // Pythagore red — countries in the region
const WAF_FILL_HOVER = "#991D14"; // darker red on hover
const NEUTRAL_FILL = "#E4E4E7";   // zinc-200 — non-WAF African countries
const NEUTRAL_HOVER = "#D4D4D8";  // zinc-300



// Display order under the Country Manager: System Engineer Team → Sales Team
// → Support Team. Each group declares which roles it absorbs from
// WAF_TEAM.contacts (or channelRenewal) and its visual accent.
const TEAM_GROUPS = [
  {
    key: "system-engineer",
    title: "Team System Engineer",
    icon: Cpu,
    source: "contacts",
    roles: ["Presales Lead", "System Engineer"],
    headerBg: "bg-sky-50/60",
    headerText: "text-sky-900",
    roleAccent: "text-sky-700",
  },
  {
    key: "sales",
    title: "Team Sales",
    icon: Briefcase,
    source: "contacts",
    roles: ["Major Account Manager", "Regional Account Manager"],
    headerBg: "bg-zinc-50",
    headerText: "text-zinc-700",
    roleAccent: "text-zinc-500",
  },
  {
    key: "support",
    title: "Team Support",
    icon: LifeBuoy,
    source: "channelRenewal",
    roles: ["Channel Account Manager", "ISR Renewal"],
    headerBg: "bg-amber-50/60",
    headerText: "text-amber-900",
    roleAccent: "text-amber-700",
  },
];

export default function Contact() {
  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState("WAF"); // "WAF" or country.name
  // Accordion state — each team starts collapsed; user expands the one they
  // need. Keys match TEAM_GROUPS[].key.
  const [openTeams, setOpenTeams] = useState({
    "system-engineer": false,
    sales: false,
    support: false,
  });
  const toggleTeam = (k) =>
    setOpenTeams((prev) => ({ ...prev, [k]: !prev[k] }));

  const focusedCountry = useMemo(
    () => WAF_COUNTRIES.find((c) => c.name === focused),
    [focused]
  );
  const showRegion = focused === "WAF" || !focusedCountry;

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12" data-testid="contact-page">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D42B1E]">
          Contact
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 mt-1">
          Équipe Fortinet — Afrique
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Cliquez sur un pays mis en couleur pour afficher l'équipe locale Fortinet.
          La région <span className="font-semibold text-zinc-900">West Africa French (WAF)</span> couvre
          {" "}{WAF_COUNTRIES.length}{" "}pays francophones d'Afrique de l'Ouest et Centrale.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* MAP */}
        <div className="lg:col-span-3 border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="contact-map-card">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
              Carte interactive
            </h3>
            <span className="text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
              Source : Natural Earth 1:110m
            </span>
          </div>
          <div className="bg-[#FAFAFA] p-2 sm:p-4">
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 380, center: [18, 1] }}
              width={800}
              height={680}
              style={{ width: "100%", height: "auto" }}
              data-testid="contact-africa-map"
            >
              <ZoomableGroup zoom={1} minZoom={1} maxZoom={4}>
                <Geographies geography={africaTopo}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const name = geo.properties.name;
                      const isWaf = WAF_COUNTRY_NAMES.has(name);
                      const isHovered = hovered === name;
                      const isFocused = focused === name;
                      const fill = isWaf
                        ? isHovered || isFocused
                          ? WAF_FILL_HOVER
                          : WAF_FILL
                        : isHovered
                        ? NEUTRAL_HOVER
                        : NEUTRAL_FILL;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() => setHovered(name)}
                          onMouseLeave={() => setHovered(null)}
                          onClick={() => isWaf && setFocused(name)}
                          data-testid={`country-${(geo.properties.name || "").replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          style={{
                            default: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.6,
                              outline: "none",
                              cursor: isWaf ? "pointer" : "default",
                              transition: "fill 150ms ease",
                            },
                            hover: { fill, outline: "none" },
                            pressed: { fill, outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>

                {/* Off-shore island markers (Cabo Verde, São Tomé) — clickable too */}
                {WAF_COUNTRIES.filter((c) => !c.onMap).map((c) => {
                  const active = focused === c.name || hovered === c.name;
                  return (
                    <Marker
                      key={c.iso2}
                      coordinates={c.coords}
                      onMouseEnter={() => setHovered(c.name)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => setFocused(c.name)}
                      style={{ cursor: "pointer" }}
                      data-testid={`marker-${c.iso2}`}
                    >
                      <circle
                        r={6}
                        fill={active ? WAF_FILL_HOVER : WAF_FILL}
                        stroke="#FFFFFF"
                        strokeWidth={1.5}
                      />
                      <text
                        textAnchor="middle"
                        y={-10}
                        fontSize={9}
                        fontFamily="ui-sans-serif"
                        fill="#27272a"
                        style={{ pointerEvents: "none" }}
                      >
                        {c.label}
                      </text>
                    </Marker>
                  );
                })}
              </ZoomableGroup>
            </ComposableMap>

            {/* Tooltip pill at the bottom */}
            <div className="mt-2 px-3 py-2 text-[11px] text-zinc-600 border-t border-zinc-100 flex items-center justify-between">
              <span data-testid="map-hover-label">
                {hovered ? (
                  WAF_COUNTRY_NAMES.has(hovered) ? (
                    <span><span className="inline-block h-2 w-2 rounded-full bg-[#D42B1E] mr-2" />Région WAF · {WAF_COUNTRIES.find((c) => c.name === hovered)?.label}</span>
                  ) : (
                    <span><span className="inline-block h-2 w-2 rounded-full bg-zinc-300 mr-2" />{hovered}</span>
                  )
                ) : (
                  <span className="text-zinc-400">Survolez un pays pour le mettre en évidence.</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setFocused("WAF")}
                className={`text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-1 transition-colors ${
                  focused !== "WAF"
                    ? "bg-[#D42B1E] text-white hover:bg-[#991D14]"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
                data-testid="reset-region-button"
              >
                {focused !== "WAF" ? "← Réinitialiser la carte" : "Réinitialiser"}
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="px-5 py-3 border-t border-zinc-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px]" data-testid="contact-map-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm" style={{ background: WAF_FILL }} />
              Région WAF ({WAF_COUNTRIES.length} pays)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm" style={{ background: NEUTRAL_FILL }} />
              Hors couverture WAF
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: WAF_FILL }} />
              Île WAF (marqueur)
            </span>
          </div>
        </div>

        {/* SIDE PANEL */}
        <div className="lg:col-span-2 space-y-4" data-testid="contact-side-panel">
          {/* Region card */}
          <div className="border border-zinc-200 bg-white rounded-sm">
            <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
              <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-500">Région</p>
              <span className="font-mono text-[10px] px-2 py-0.5 bg-[#D42B1E] text-white rounded-sm">
                {WAF_TEAM.region.slug}
              </span>
            </div>
            <div className="p-5 space-y-3">
              <h2 className="text-lg font-semibold text-zinc-900">{WAF_TEAM.region.name}</h2>
              <div className="text-xs text-zinc-600 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-zinc-400" />
                  <span>HQ : <span className="font-medium text-zinc-800">{WAF_TEAM.region.headquarters}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-zinc-400" />
                  <span>Fuseau : <span className="font-medium text-zinc-800">{WAF_TEAM.region.timezone}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-zinc-400" />
                  <span>{WAF_COUNTRIES.length} pays couverts</span>
                </div>
              </div>

              {/* Countries chips */}
              <div className="pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Pays couverts</p>
                <div className="flex flex-wrap gap-1.5" data-testid="waf-country-chips">
                  {WAF_COUNTRIES.map((c) => {
                    const active = focused === c.name;
                    return (
                      <button
                        type="button"
                        key={c.iso2}
                        onClick={() => setFocused(c.name)}
                        onMouseEnter={() => setHovered(c.name)}
                        onMouseLeave={() => setHovered(null)}
                        className={`px-2 py-1 rounded-sm text-[11px] font-medium border transition-all ${
                          active
                            ? "bg-[#D42B1E] text-white border-[#D42B1E]"
                            : "bg-white text-zinc-700 border-zinc-200 hover:border-[#D42B1E] hover:text-[#D42B1E]"
                        }`}
                        data-testid={`chip-${c.iso2}`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Selected country card */}
          {!showRegion && focusedCountry && (
            <div className="border border-[#D42B1E]/30 bg-[#D42B1E]/5 rounded-sm overflow-hidden" data-testid="selected-country-card">
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D42B1E]">Pays sélectionné</p>
                    <h3 className="mt-1 text-base font-semibold text-zinc-900">
                      {focusedCountry.label}
                    </h3>
                    <p className="text-[11px] text-zinc-600 mt-0.5">
                      Capitale : <span className="font-medium text-zinc-800">{focusedCountry.capital}</span> · ISO : {focusedCountry.iso2}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFocused("WAF")}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-[#D42B1E] hover:text-[#991D14] hover:bg-white border border-[#D42B1E]/40 hover:border-[#D42B1E] rounded-sm px-2.5 py-1.5 transition-colors"
                    data-testid="selected-country-reset-btn"
                    title="Revenir à la vue régionale WAF"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Retour
                  </button>
                </div>
              </div>

              {/* Country-specific Account Managers */}
              {(WAF_COUNTRY_AMS[focusedCountry.name] || []).length > 0 ? (
                <div className="border-t border-[#D42B1E]/20 bg-white">
                  <div className="px-4 py-2 border-b border-zinc-100">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                      Managing Team - {focusedCountry.label}
                    </p>
                  </div>
                  <ul className="divide-y divide-zinc-100" data-testid="country-ams-list">
                    {WAF_COUNTRY_AMS[focusedCountry.name].map((am, idx) => (
                      <li key={idx} className="p-4" data-testid={`country-am-${idx}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-zinc-900">{am.name}</p>
                            <p className="text-[11px] text-zinc-600 mt-0.5">{am.role}</p>
                          </div>
                          <span className="shrink-0 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-[#D42B1E] text-white uppercase tracking-wide">
                            {focusedCountry.iso2}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-col gap-1.5 text-xs">
                          {am.phone && (
                            <div className="inline-flex items-center gap-1.5">
                              <a
                                href={waLink(am.phone)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Discuter sur WhatsApp"
                                aria-label={`WhatsApp ${am.name}`}
                                data-testid={`country-am-whatsapp-${idx}`}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#25D366] text-white hover:opacity-90 transition-opacity shrink-0"
                              >
                                <WhatsappIcon className="h-3 w-3" />
                              </a>
                              <a
                                href={`tel:${am.phone.replace(/\s+/g, "")}`}
                                className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                                data-testid={`country-am-phone-${idx}`}
                              >
                                <Phone className="h-3 w-3" />
                                <span className="font-mono">{am.phone}</span>
                              </a>
                            </div>
                          )}
                          {am.email && (
                            <a
                              href={`mailto:${am.email}`}
                              className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                            >
                              <Mail className="h-3 w-3" />
                              <span className="font-mono">{am.email}</span>
                            </a>
                          )}
                          {am.linkedin && (
                            <a
                              href={am.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                            >
                              <Linkedin className="h-3 w-3" />
                              LinkedIn
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="border-t border-[#D42B1E]/20 bg-white px-4 py-3 text-[11px] text-zinc-500" data-testid="country-no-ams">
                  Account Manager local en cours de communication — voir l'équipe régionale ci-dessous.
                </div>
              )}
            </div>
          )}

          {/* Contacts card */}
          <div className="border border-zinc-200 bg-white rounded-sm" data-testid="waf-contacts-card">
            {/* Country Manager — highlighted at the top */}
            {WAF_TEAM.countryManager && (
              <div
                className="px-5 py-4 border-b border-[#D42B1E]/20"
                style={{ background: "linear-gradient(90deg, rgba(212,43,30,0.08), rgba(212,43,30,0.02) 60%, rgba(255,255,255,1))" }}
                data-testid="waf-country-manager"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#D42B1E]">
                      {WAF_TEAM.countryManager.role}
                    </p>
                    <p className="mt-0.5 text-base font-semibold text-zinc-900">
                      {WAF_TEAM.countryManager.name}
                    </p>
                  </div>
                  <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-[#D42B1E] text-white tracking-wide">
                    LEAD
                  </span>
                </div>
                <a
                  href={`mailto:${WAF_TEAM.countryManager.email}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-[#D42B1E]"
                  data-testid="waf-country-manager-email"
                >
                  <Mail className="h-3 w-3" />
                  <span className="font-mono">{WAF_TEAM.countryManager.email}</span>
                </a>
              </div>
            )}

            {/* Grouped by team — System Engineer, Sales, Support (accordion) */}
            {TEAM_GROUPS.map((group) => {
              const members = (group.source === "channelRenewal"
                ? (WAF_TEAM.channelRenewal || [])
                : (WAF_TEAM.contacts || [])
              ).filter((c) => group.roles.includes(c.role));
              if (members.length === 0) return null;
              const isOpen = !!openTeams[group.key];
              return (
                <div key={group.key} data-testid={`waf-team-${group.key}`}>
                  <button
                    type="button"
                    onClick={() => toggleTeam(group.key)}
                    aria-expanded={isOpen}
                    className={`w-full flex items-center justify-between px-5 py-3 border-y border-zinc-100 ${group.headerBg} hover:brightness-95 transition cursor-pointer`}
                    data-testid={`waf-team-toggle-${group.key}`}
                  >
                    <p className={`text-[11px] font-semibold tracking-[0.15em] uppercase flex items-center gap-1.5 ${group.headerText}`}>
                      <group.icon className="h-3 w-3" />
                      {group.title}
                      <span className="ml-1 text-[10px] font-mono text-zinc-400 normal-case tracking-wide">
                        {members.length} membre{members.length > 1 ? "s" : ""}
                      </span>
                    </p>
                    <ChevronDown
                      className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <ul className="divide-y divide-zinc-100" data-testid={`waf-team-list-${group.key}`}>
                      {members.map((c, idx) => (
                        <li key={`${group.key}-${idx}`} className="p-4" data-testid={`team-${group.key}-${idx}`}>
                          <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${group.roleAccent}`}>
                            {c.role}
                          </p>
                          <p className="mt-1 text-sm font-medium text-zinc-900">{c.name}</p>
                          <div className="mt-2 flex flex-col gap-1.5 text-xs">
                            {c.email && (
                              <a
                                href={`mailto:${c.email}`}
                                className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                                data-testid={`team-${group.key}-email-${idx}`}
                              >
                                <Mail className="h-3 w-3" />
                                <span className="font-mono">{c.email}</span>
                              </a>
                            )}
                            {c.phone && (
                              <div className="inline-flex items-center gap-1.5">
                                <a
                                  href={waLink(c.phone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Discuter sur WhatsApp"
                                  aria-label={`WhatsApp ${c.name}`}
                                  data-testid={`team-${group.key}-whatsapp-${idx}`}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#25D366] text-white hover:opacity-90 transition-opacity shrink-0"
                                >
                                  <WhatsappIcon className="h-3 w-3" />
                                </a>
                                <a
                                  href={`tel:${c.phone.replace(/\s+/g, "")}`}
                                  className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                                  data-testid={`team-${group.key}-phone-${idx}`}
                                >
                                  <Phone className="h-3 w-3" />
                                  <span className="font-mono">{c.phone}</span>
                                </a>
                              </div>
                            )}
                            {c.linkedin && (
                              <a
                                href={c.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#D42B1E]"
                              >
                                <Linkedin className="h-3 w-3" />
                                LinkedIn
                              </a>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
